import { KeyRound, RefreshCw, Smartphone, Unlink } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dialog,
  Field,
  Spinner,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  usePatientAppSignInAction,
  usePatientAppSignInMethods,
} from '@/features/patients/api';
import {
  APP_SIGN_IN_REASON_LIMITS,
  type PatientAppSignInAccount,
  type PatientDetail,
} from '@/features/patients/types';
import { isApiError } from '@/lib/api';
import {
  appAccountDay,
  appAccountTime,
  appLinkMethodLabel,
} from './app-account-format';
import styles from './PatientAppAccount.module.css';
import { UnlinkAppAccountDialog } from './UnlinkAppAccountDialog';

const noop = () => {};
const { min: MIN_REASON, max: MAX_REASON } = APP_SIGN_IN_REASON_LIMITS;

type PasswordAction = 'disable' | 'enable';

function PasswordStatus({ account }: { account: PatientAppSignInAccount }) {
  const { t } = useTranslation();
  if (account.password === 'disabled_by_clinic')
    return (
      <Badge tone="danger" dot>
        {t('patientAppSignIn.passwordDisabled')}
      </Badge>
    );
  if (account.password === 'set')
    return (
      <Badge tone="success" dot>
        {t('patientAppSignIn.passwordSet')}
      </Badge>
    );
  return <Badge>{t('patientAppSignIn.passwordNotSet')}</Badge>;
}

function PasswordSignInDialog({
  patientId,
  account,
  action,
  onClose,
  onStale,
}: {
  patientId: string;
  account: PatientAppSignInAccount;
  action: PasswordAction;
  onClose: () => void;
  onStale: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const formId = useId();
  const changePasswordSignIn = usePatientAppSignInAction(patientId);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string>();
  const [failure, setFailure] = useState<{
    message: string;
    requestId?: string;
  } | null>(null);
  const text = reason.trim();
  const pending = changePasswordSignIn.isPending;
  const disabling = action === 'disable';

  async function submit() {
    setFailure(null);
    if (text.length < MIN_REASON) {
      setReasonError(t('patientAppSignIn.reasonTooShort', { min: MIN_REASON }));
      return;
    }
    try {
      await changePasswordSignIn.mutateAsync({
        accountId: account.accountId,
        action,
        reason: text,
      });
      toast.show({
        tone: 'success',
        title: t(
          disabling
            ? 'patientAppSignIn.disabledToast'
            : 'patientAppSignIn.enabledToast',
        ),
        body: t(
          disabling
            ? 'patientAppSignIn.disabledToastBody'
            : 'patientAppSignIn.enabledToastBody',
        ),
      });
      onClose();
    } catch (error) {
      if (!isApiError(error)) {
        setFailure({ message: t('patientAppSignIn.changeFailed') });
        return;
      }
      const fieldError = error.fieldError('reason');
      if (fieldError) {
        setReasonError(fieldError);
        return;
      }
      if (error.code === 'APP_ACCOUNT_NOT_FOUND') {
        onStale();
        toast.show({
          tone: 'error',
          title: t('patientAppSignIn.changedElsewhere'),
          body: error.requestId
            ? `${t('patientAppSignIn.changedElsewhereBody')} ${t('common.requestId', { id: error.requestId })}`
            : t('patientAppSignIn.changedElsewhereBody'),
        });
        onClose();
        return;
      }
      setFailure({
        message:
          error.code === 'PERMISSION_DENIED'
            ? t('patientAppSignIn.permissionDenied')
            : error.message,
        requestId: error.requestId,
      });
    }
  }

  return (
    <Dialog
      open
      onClose={pending ? noop : onClose}
      variant="modal"
      dismissible={false}
      title={t(
        disabling
          ? 'patientAppSignIn.disableTitle'
          : 'patientAppSignIn.enableTitle',
      )}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            {t('patientAppSignIn.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant={disabling ? 'danger' : 'primary'}
            loading={pending}
          >
            {t(
              disabling
                ? 'patientAppSignIn.disableSubmit'
                : 'patientAppSignIn.enableSubmit',
            )}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={styles.dialog}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!pending) void submit();
        }}
      >
        <p className={styles.summary}>
          {t(
            disabling
              ? 'patientAppSignIn.disableBody'
              : 'patientAppSignIn.enableBody',
          )}
        </p>
        <p className={styles.summary}>
          {t('patientAppSignIn.phone')}:{' '}
          <span className="mono">
            {account.phoneMasked ?? t('patientAppSignIn.phoneUnavailable')}
          </span>
        </p>
        {failure && (
          <p className={styles.error} role="alert">
            {failure.message}
            {failure.requestId && (
              <span>{t('common.requestId', { id: failure.requestId })}</span>
            )}
          </p>
        )}
        <Field
          label={t('patientAppSignIn.reason')}
          hint={t('patientAppSignIn.reasonHint', {
            min: MIN_REASON,
            max: MAX_REASON,
          })}
          error={reasonError}
        >
          <textarea
            className={styles.textarea}
            rows={3}
            maxLength={MAX_REASON}
            required
            value={reason}
            disabled={pending}
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(undefined);
            }}
          />
        </Field>
        <span className={styles.counter}>
          {text.length} / {MAX_REASON}
        </span>
      </form>
    </Dialog>
  );
}

function SignInMethods({
  patientId,
  canManage,
}: {
  patientId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const methods = usePatientAppSignInMethods(patientId);
  const [action, setAction] = useState<{
    account: PatientAppSignInAccount;
    kind: PasswordAction;
  } | null>(null);
  const error = isApiError(methods.error) ? methods.error : undefined;

  return (
    <section
      className={styles.signInMethods}
      aria-label={t('patientAppSignIn.title')}
    >
      <h4 className={styles.signInTitle}>
        <KeyRound size={14} aria-hidden="true" />
        {t('patientAppSignIn.title')}
      </h4>
      {methods.isPending ? (
        <span className={styles.loading}>
          <Spinner size={16} label={t('patientAppSignIn.loading')} />
        </span>
      ) : error?.code === 'APP_ACCOUNT_NOT_FOUND' ||
        error?.code === 'PATIENT_NOT_FOUND' ? (
        <div className={styles.methodsMessage} role="status">
          <span>{t('patientAppSignIn.noLongerAvailable')}</span>
          {error.requestId && (
            <span>{t('common.requestId', { id: error.requestId })}</span>
          )}
        </div>
      ) : methods.error ? (
        <div className={styles.methodsMessage} role="alert">
          <span>{t('patientAppSignIn.loadFailed')}</span>
          {error?.requestId && (
            <span>{t('common.requestId', { id: error.requestId })}</span>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            onClick={() => void methods.refetch()}
          >
            {t('patientAppSignIn.retry')}
          </Button>
        </div>
      ) : methods.data?.accounts.length === 0 ? (
        <div className={styles.methodsMessage} role="status">
          <strong>{t('patientAppSignIn.empty')}</strong>
          <span>{t('patientAppSignIn.emptyBody')}</span>
        </div>
      ) : (
        <div className={styles.methodList}>
          {methods.data?.accounts.map((account) => (
            <div className={styles.methodCard} key={account.accountId}>
              <div className={styles.methodRow}>
                <span className={styles.methodLabel}>
                  {t('patientAppSignIn.phone')}
                </span>
                <span className="mono">
                  {account.phoneMasked ??
                    t('patientAppSignIn.phoneUnavailable')}
                </span>
                {account.phoneMasked && (
                  <Badge tone="success">{t('patientAppSignIn.verified')}</Badge>
                )}
              </div>
              <div className={styles.methodRow}>
                <span className={styles.methodLabel}>
                  {t('patientAppSignIn.email')}
                </span>
                {account.emailMasked ? (
                  <>
                    <span className="mono">{account.emailMasked}</span>
                    <Badge tone="success">
                      {t('patientAppSignIn.confirmed')}
                    </Badge>
                  </>
                ) : (
                  <Badge>{t('patientAppSignIn.emailNotSet')}</Badge>
                )}
              </div>
              <div className={styles.methodRow}>
                <span className={styles.methodLabel}>
                  {t('patientAppSignIn.password')}
                </span>
                <PasswordStatus account={account} />
              </div>
              {account.passwordSetAt && (
                <span className={styles.methodMeta}>
                  {t('patientAppSignIn.setAt', {
                    time: appAccountTime(account.passwordSetAt),
                  })}
                </span>
              )}
              {account.passwordDisabledAt && (
                <span className={styles.methodMeta}>
                  {t('patientAppSignIn.disabledAt', {
                    time: appAccountTime(account.passwordDisabledAt),
                  })}
                </span>
              )}
              {account.passwordDisabledReason && (
                <span className={styles.methodMeta}>
                  {t('patientAppSignIn.disabledReason', {
                    reason: account.passwordDisabledReason,
                  })}
                </span>
              )}
              {account.lockedUntil && (
                <Badge tone="warning">
                  {t('patientAppSignIn.lockedUntil', {
                    time: appAccountTime(account.lockedUntil),
                  })}
                </Badge>
              )}
              {canManage && account.password === 'set' && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setAction({ account, kind: 'disable' })}
                >
                  {t('patientAppSignIn.disable')}
                </Button>
              )}
              {canManage && account.password === 'disabled_by_clinic' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAction({ account, kind: 'enable' })}
                >
                  {t('patientAppSignIn.enable')}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {action && (
        <PasswordSignInDialog
          patientId={patientId}
          account={action.account}
          action={action.kind}
          onClose={() => setAction(null)}
          onStale={() => void methods.refetch()}
        />
      )}
    </section>
  );
}

/**
 * RL-1118/RL-1185: linked app-account identity and unlink action.
 * RL-1325: sign-in methods plus the separately permissioned password switch.
 */
export function PatientAppAccount({
  patient,
}: {
  patient: Pick<
    PatientDetail,
    'id' | 'status' | 'version' | 'ekyc' | 'appAccount' | 'appAccountDeletedAt'
  >;
}) {
  const { hasPermission } = useSession();
  const [unlinking, setUnlinking] = useState(false);
  const account = patient.appAccount;
  if (!account)
    return (
      <span className={styles.meta}>
        {patient.appAccountDeletedAt
          ? `No app account. App account deleted on ${appAccountDay(patient.appAccountDeletedAt)}.`
          : 'No app account linked'}
      </span>
    );
  return (
    <div
      className={styles.account}
      role="group"
      aria-label="Linked app account"
    >
      <span className={styles.phone}>
        <Smartphone size={14} aria-hidden="true" />
        <span className="mono">{account.phone ?? 'Phone not shown'}</span>
        {account.identityPending ? (
          <Badge tone="warning" dot>
            Identity pending
          </Badge>
        ) : (
          <Badge tone="success" dot>
            Linked
          </Badge>
        )}
      </span>
      <span className={styles.meta}>
        {appLinkMethodLabel(account.method)} · linked{' '}
        {appAccountTime(account.linkedAt)}
      </span>
      {account.identityPending && (
        <span className={styles.pending}>
          The app shows no clinical data and sends no notifications for this
          record until counter eKYC confirms the app account is the
          patient&apos;s.
        </span>
      )}
      <SignInMethods
        patientId={patient.id}
        canManage={
          hasPermission('patient_app_sign_in:manage') &&
          patient.status !== 'merged'
        }
      />
      {hasPermission('patients:update') && patient.status !== 'merged' && (
        <span className={styles.actions}>
          <Button
            size="sm"
            variant="ghost"
            icon={<Unlink size={14} />}
            onClick={() => setUnlinking(true)}
          >
            Unlink app account
          </Button>
        </span>
      )}
      {unlinking && (
        <UnlinkAppAccountDialog
          patient={patient}
          account={account}
          onClose={() => setUnlinking(false)}
        />
      )}
    </div>
  );
}
